import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Cpu, RotateCw, Layers, Disc, RectangleHorizontal, Wrench, DraftingCompass } from "lucide-react";
import Reveal from "../components/Reveal";
import SectionHeading from "../components/SectionHeading";
import Marquee from "../components/Marquee";
import RfqBanner from "../components/RfqBanner";
import PlaceholderImage from "../components/PlaceholderImage";
import { CAPABILITIES, SELECTED_WORK_SLOTS, INDUSTRIES, CLIENT_LOGOS, PRODUCTS, PROCESS_STEPS } from "../data/content";

const ICONS = { Cpu, RotateCw, Layers, Disc, RectangleHorizontal, Wrench, DraftingCompass };

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.85, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 700], [0, 100]);

  return (
    <section data-testid="hero-section" className="relative overflow-hidden bg-[#0B1329]">
      <motion.img
        src="/images/hero.jpg"
        alt="CNC machining centre manufacturing precision metal components at an industrial facility"
        style={{ y }}
        className="absolute inset-0 h-[115%] w-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0B1329]/95 via-[#0B1329]/75 to-[#0B1329]/35" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-28 md:py-40">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="text-xs font-semibold uppercase tracking-[0.25em] text-blue-400"
        >
          Est. 1995 · Hyderabad, India
        </motion.p>
        <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.08]">
          <MaskedLine delay={0.25}>Precision Engineering</MaskedLine>
          <MaskedLine delay={0.4}>&amp; Manufacturing</MaskedLine>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
          className="mt-6 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed"
        >
          Tooling, Dies, machined components and custom parts manufactured to customer
          drawings and technical specifications.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.85 }}
          className="mt-9 flex flex-wrap gap-4"
        >
          <Link
            to="/contact"
            data-testid="hero-rfq-button"
            className="inline-flex items-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-sm font-semibold px-7 py-3.5 rounded-sm transition-colors duration-150"
          >
            Request a Quote <ArrowRight size={16} />
          </Link>
          <Link
            to="/products"
            data-testid="hero-view-products-button"
            className="inline-flex items-center gap-2 border border-white/50 text-white hover:bg-white/10 text-sm font-semibold px-7 py-3.5 rounded-sm transition-colors duration-150"
          >
            View Products
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

const AboutBrief = () => (
  <section data-testid="home-about-section" className="py-16 md:py-24">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
      <Reveal>
        <div className="relative">
          <img
            src="/images/company.jpg"
            alt="Radiant Machine Tools workshop — precision CNC machining and reliable conventional machining capabilities"
            className="w-full aspect-[8/5] object-cover border border-slate-200"
            loading="lazy"
          />
          <div className="absolute -bottom-5 -right-2 sm:right-6 bg-[#0B1329] text-white px-6 py-4 border-l-4 border-[#2563EB]">
            <p className="text-2xl font-bold">Since 1995</p>
            <p className="text-xs uppercase tracking-widest text-slate-400 mt-1">Hyderabad, Telangana</p>
          </div>
        </div>
      </Reveal>
      <Reveal delay={0.1}>
        <SectionHeading
          eyebrow="About RMT"
          title="Engineering Experience Built Around Precision"
        />
        <p className="mt-4 text-base text-slate-600 leading-relaxed">
          Radiant Machine Tools is an engineering and manufacturing company established in 1995
          and based in Hyderabad, Telangana, India. The company manufactures tooling, Dies, machined
          components and custom industrial parts.
        </p>
        <p className="mt-4 text-base text-slate-600 leading-relaxed">
          We work with customer drawings and technical specifications — including dimensions,
          tolerances, material requirements and surface-finish requirements — to manufacture
          components that match what our customers have engineered.
        </p>
        <Link
          to="/about"
          data-testid="home-about-cta"
          className="mt-7 inline-flex items-center gap-2 border border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white text-sm font-semibold px-6 py-3 rounded-sm transition-colors duration-150"
        >
          Learn More About Us <ArrowRight size={16} />
        </Link>
      </Reveal>
    </div>
  </section>
);

const ProductsSection = () => (
  <section data-testid="home-products-section" className="py-16 md:py-24">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <Reveal>
        <SectionHeading
          eyebrow="Product Range"
          title="Our Products"
          description="High-quality tooling, Dies and precision components for industrial applications."
        />
      </Reveal>
      <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {PRODUCTS.slice(0, 8).map((p, i) => (
          <Reveal key={p.id} delay={(i % 4) * 0.05}>
            <div
              data-testid={`product-card-${p.id}`}
              className="h-full bg-white border border-slate-200 rounded-sm overflow-hidden group"
            >
              <div className="overflow-hidden">
                <img
                  src={p.img}
                  alt={p.alt}
                  loading="lazy"
                  className="h-40 w-full object-cover group-hover:scale-[1.03] transition-transform duration-300"
                />
              </div>
              <div className="p-5">
                <h3 className="text-base font-semibold text-slate-900">{p.name}</h3>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed line-clamp-2">{p.desc}</p>
                <Link
                  to={`/contact?product=${encodeURIComponent(p.name)}`}
                  data-testid={`product-enquire-button-${p.id}`}
                  className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#2563EB] hover:text-[#1D4ED8]"
                >
                  Enquire Now <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal className="mt-10 text-center">
        <Link
          to="/products"
          data-testid="view-all-products-button"
          className="inline-flex items-center gap-2 border border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white text-sm font-semibold px-7 py-3 rounded-sm transition-colors duration-150"
        >
          View All Products <ArrowRight size={16} />
        </Link>
      </Reveal>
    </div>
  </section>
);

const CapabilitiesSection = () => (
  <section data-testid="home-capabilities-section" className="py-16 md:py-24 bg-[#F8FAFC] border-y border-slate-200">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <Reveal>
        <SectionHeading
          eyebrow="What We Do"
          title="Our Capabilities"
          description="Advanced manufacturing capabilities to meet your precision requirements."
        />
      </Reveal>
      <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {CAPABILITIES.map((cap, i) => {
          const Icon = ICONS[cap.icon];
          return (
            <Reveal key={cap.id} delay={i * 0.05}>
              <div
                data-testid={`capability-card-${cap.id}`}
                className="h-full bg-white border border-slate-200 p-6 rounded-sm hover:border-[#2563EB] hover:-translate-y-0.5 transition-all duration-200"
              >
                <div className="h-11 w-11 flex items-center justify-center bg-blue-50 border border-blue-100 rounded-sm">
                  <Icon size={20} className="text-[#2563EB]" strokeWidth={1.75} />
                </div>
                <h3 className="mt-4 text-base font-semibold text-slate-900">{cap.name}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{cap.desc}</p>
              </div>
            </Reveal>
          );
        })}
        <Reveal delay={0.35}>
          <Link
            to="/contact"
            data-testid="capability-cta-card"
            className="h-full min-h-[180px] flex flex-col justify-between bg-[#0B1329] p-6 rounded-sm group"
          >
            <p className="text-base font-semibold text-white leading-snug">
              Have a custom machining requirement?
            </p>
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-blue-400 group-hover:gap-3 transition-all duration-200">
              Request a Quote <ArrowRight size={15} />
            </span>
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

const SelectedWorkSection = () => (
  <section data-testid="selected-work-section" className="py-16 md:py-20">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <Reveal>
        <SectionHeading
          eyebrow="Manufacturing Experience"
          title="Selected Work"
          description="A selection of components and engineering work manufactured to customer drawings, specifications and requirements."
        />
      </Reveal>
      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {SELECTED_WORK_SLOTS.map((w, i) => (
          <Reveal key={w.id} delay={i * 0.05}>
            <article
              data-testid={`selected-work-card-${w.id}`}
              className="h-full bg-white border border-slate-200 rounded-sm overflow-hidden"
            >
              {w.img ? (
                <div className="overflow-hidden" data-testid={`selected-work-image-${w.id}`}>
                  <img src={w.img} alt={w.alt} loading="lazy" className="aspect-[4/3] w-full object-cover" />
                </div>
              ) : (
                <PlaceholderImage label="Component photograph" testId={`selected-work-image-${w.id}`} />
              )}
              <div className="p-5">
                <div className="h-0.5 w-8 bg-[#2563EB]" aria-hidden="true" />
                <h3 className="mt-3 text-base font-semibold text-slate-900">{w.name}</h3>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{w.desc}</p>
                <p className="mt-2 text-xs font-mono text-slate-500">{w.process}</p>
              </div>
            </article>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const IndustriesClienteleSection = () => (
  <section data-testid="home-industries-section" className="py-16 md:py-20 bg-[#F8FAFC] border-y border-slate-200">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <Reveal>
        <SectionHeading
          eyebrow="Application Areas"
          title="Industries Served"
          description="Our precision components, tooling and machined parts support a range of industrial applications across diverse sectors."
        />
      </Reveal>
      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {INDUSTRIES.map((ind, i) => (
          <Reveal key={ind.id} delay={(i % 3) * 0.05}>
            <div
              data-testid={`industry-slot-${ind.id}`}
              className="h-full bg-white border border-slate-200 p-5 rounded-sm hover:border-[#2563EB] transition-colors duration-200"
            >
              <span className="text-xs font-mono font-semibold text-[#2563EB]">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="mt-2 text-base font-semibold text-slate-900">{ind.name}</h3>
              <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{ind.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <div className="mt-14 pt-10 border-t border-slate-200">
        <Reveal>
          <SectionHeading
            eyebrow="Trusted Industry Partners"
            title="Our Clients"
            description="Working with industrial and engineering customers across a range of manufacturing requirements."
          />
        </Reveal>
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
          {CLIENT_LOGOS.map((c) => (
            <div
              key={c.id}
              data-testid={`client-logo-${c.id}`}
              title={c.name}
              className="h-28 bg-white border border-slate-200 rounded-sm flex items-center justify-center px-5 hover:border-slate-400 transition-colors duration-150"
            >
              <img
                src={`/images/clients/${c.id}.png`}
                alt={`${c.name} logo`}
                loading="lazy"
                className="max-h-14 w-auto max-w-full object-contain"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const ProcessSection = () => (
  <section data-testid="process-section" className="py-16 md:py-24 border-b border-slate-200">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <Reveal>
        <SectionHeading
          eyebrow="How We Work"
          title="Our Manufacturing Approach"
          description="A straightforward, drawing-driven process from requirement to delivery."
        />
      </Reveal>
      <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
        {PROCESS_STEPS.map((step, i) => (
          <Reveal key={step.no} delay={i * 0.07}>
            <div data-testid={`process-step-${step.no}`} className="relative">
              <div className="flex items-center gap-3">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center bg-[#0B1329] text-white text-sm font-bold font-mono rounded-sm">
                  {step.no}
                </span>
                {i < PROCESS_STEPS.length - 1 && (
                  <span className="hidden lg:block h-px flex-1 bg-slate-300" aria-hidden="true" />
                )}
              </div>
              <h3 className="mt-4 text-sm font-semibold text-slate-900">{step.title}</h3>
              <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{step.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Home = () => (
  <>
    <Hero />
    <Marquee />
    <AboutBrief />
    <ProductsSection />
    <CapabilitiesSection />
    <SelectedWorkSection />
    <IndustriesClienteleSection />
    <ProcessSection />
    <RfqBanner />
  </>
);

export default Home;
